// "Demo Arena" — the original sales-demo operator: a fictional event venue with
// a structured deck, a surface overflow lot, and a small premium lot. Every value
// here reproduces the pre-refactor hard-coded demo exactly, so the Demo Arena
// presentation is unchanged.

import type { DemoClient } from './types'

export const ARENA_CLIENT_ID = 'demo-arena'

const FAC_DECK = 'demo-arena-deck'
const FAC_NORTH = 'demo-north-lots'

export const arenaClient: DemoClient = {
  id: ARENA_CLIENT_ID,
  name: 'Demo Arena',
  timezone: 'America/New_York',
  // Keep the console Parkify-purple rather than white-labeling to an arena color.
  accent: '#7F3F98',
  user: { email: 'alex.rivera@demo-arena.com', name: 'Alex Rivera', role: 'manager' },
  contactName: 'Alex Rivera',
  contactEmail: 'alex.rivera@demo-arena.com',
  contactPhone: '(404) 555-0142',
  notes: 'Sales demo tenant — fictional event venue.',
  stripeAccountId: 'acct_demoArena0001',
  platformFeeFlatCents: 30,
  customerFees: {
    adminFeeCents: 0,
    serviceFeePassThrough: true,
    serviceFeeBps: 290,
    serviceFeeFixedCents: 30,
  },
  city: 'Atlanta',
  state: 'GA',
  zipCode: '30313',
  zones: [
    {
      id: 'za-main-deck', name: 'Main Deck', displayName: 'MD1', zoneNumber: '1',
      type: 'garage', totalSpaces: 1400, transientCapacity: 1200, monthlyCapacity: 200,
      hourlyCents: 400, additionalHourCents: 400,
      rateChoices: [
        { choiceId: '1h', durationMins: 60 }, { choiceId: '2h', durationMins: 120 },
        { choiceId: '3h', durationMins: 180 }, { choiceId: '4h', durationMins: 240 },
        { choiceId: '6h', durationMins: 360 },
      ],
      dailyMaxCents: 4000, eventFlatCents: 3500,
      address: '1 Championship Way', lat: 33.7553, lng: -84.4006,
      description: 'Covered multi-level deck, EV charging on L2.',
      facilityId: FAC_DECK,
    },
    {
      id: 'za-north-lot', name: 'North Surface Lot', displayName: 'NL1', zoneNumber: '2',
      type: 'surface', totalSpaces: 800, transientCapacity: 760, monthlyCapacity: 40,
      hourlyCents: 300, additionalHourCents: 300,
      rateChoices: [
        { choiceId: '1h', durationMins: 60 }, { choiceId: '2h', durationMins: 120 },
        { choiceId: '3h', durationMins: 180 }, { choiceId: '4h', durationMins: 240 },
        { choiceId: '5h', durationMins: 300 }, { choiceId: '6h', durationMins: 360 },
      ],
      operatingHours: Object.fromEntries(Array.from({ length: 7 }, (_, day) => [day, { open: '06:00', close: '22:00' }])),
      dailyMaxCents: 2500, eventFlatCents: 2500,
      address: '275 North Ave NW', lat: 33.7588, lng: -84.4015,
      description: 'Surface lot, lit, event overflow.',
      facilityId: FAC_NORTH,
    },
    {
      id: 'za-vip-lot', name: 'VIP Premier Lot', displayName: 'VP1', zoneNumber: '3',
      type: 'surface', totalSpaces: 150, transientCapacity: 120, monthlyCapacity: 30,
      hourlyCents: 800, additionalHourCents: 800,
      rateChoices: [
        { choiceId: '1h', durationMins: 60 }, { choiceId: '2h', durationMins: 120 },
        { choiceId: '3h', durationMins: 180 }, { choiceId: '4h', durationMins: 240 },
        { choiceId: '8h', durationMins: 480 }, { choiceId: '24h', durationMins: 1440 },
      ],
      dailyMaxCents: 6000, eventFlatCents: 6000,
      address: '40 Premier Dr SW', lat: 33.7541, lng: -84.3998,
      description: 'Surface lot, lit, event overflow.',
      facilityId: FAC_NORTH,
    },
  ],
  // Client-wide fee model in the PRE-§3.15 flat shape — one model, both
  // products — which is what every stored record looked like before the
  // per-product reshape and what most still look like (there was no migration).
  // The console reads it as "hourly and monthly both on Model A at 18%".
  feeModelConfig: { feeModel: 'A', feeBps: 1800 },
  facilities: [
    {
      id: FAC_DECK, name: 'Arena Deck', address: '1 Championship Way', city: 'Atlanta', state: 'GA',
      // The §3.15 case: the deck's contract prices permits differently from
      // hourly — Model A on top for transient, Model D baked into the posted
      // permit price — so the property overrides the client's single model.
      feeModelConfig: {
        transient: { feeModel: 'A', feeBps: 1800 },
        monthly: { feeModel: 'D', feeBps: 1200, sameAsTransient: false },
        feeCapMonthlyCents: 1500,
      },
    },
    { id: FAC_NORTH, name: 'North Surface Lots', address: '275 North Ave NW',   city: 'Atlanta', state: 'GA' },
  ],
  passes: [],
  recurringPlanLabel: 'Monthly reserved',
  monthlyPlanCentsByType: { garage: 18500, surface: 12500 },
  upcomingEvents: [
    { title: 'Summer Concert Series — Headliner Night', daysFromNow: 6, when: '7:30 PM' },
    { title: 'Home Opener', daysFromNow: 13, when: '1:00 PM' },
  ],
  eventRates: [
    { idSuffix: 'evt-concert',   eventTitle: 'Summer Concert Series — Headliner Night', daysFromNow: 6,  durationDays: 0.2 },
    { idSuffix: 'evt-home-game', eventTitle: 'Home Opener',                              daysFromNow: 13, durationDays: 0.2 },
  ],
  finance: {
    platformFeeBps: 250,
    baseDailyCents: 130_000,
    baseDailyStepCents: 3_700,
    baseDailyJitterCents: 70_000,
    weekendLift: 1.4,
    peakEveryNDays: 7,
    peakBonusCents: 2_200_000,
    peakBonusStepCents: 31_100,
    peakBonusJitterCents: 400_000,
    baseSessions: 22,
    baseSessionsJitter: 12,
    peakSessions: 240,
    peakSessionsJitter: 30,
    todayPartialFactor: 0.45,
    zoneRevShare: [0.6, 0.26, 0.14],
    zoneSessionShare: [0.55, 0.36, 0.09],
    byChannel: [
      { channel: 'parkify', revShare: 0.82, sessionShare: 0.8,  refundShare: 0.82 },
      { channel: 'web',     revShare: 0.13, sessionShare: 0.15, refundShare: 0.13 },
      { channel: 'kiosk',   revShare: 0.05, sessionShare: 0.05, refundShare: 0 },
    ],
    byFacility: [
      { facilityId: FAC_DECK,     facilityName: 'Arena Deck',         revShare: 0.62, sessionShare: 0.62, zones: 1 },
      { facilityId: FAC_NORTH,    facilityName: 'North Surface Lots', revShare: 0.30, sessionShare: 0.30, zones: 2 },
      { facilityId: 'unassigned', facilityName: 'Unassigned',         revShare: 0.08, sessionShare: 0.08, zones: 1 },
    ],
    durationMix: [
      { bucket: '0-1h', share: 0.18 },
      { bucket: '1-3h', share: 0.27 },
      { bucket: '3-6h', share: 0.21 },
      { bucket: 'Event flat', share: 0.34 },
    ],
    hourOfDay: [2, 1, 0, 0, 0, 1, 3, 8, 14, 18, 16, 15, 17, 16, 14, 18, 26, 44, 72, 88, 64, 38, 18, 7],
    dayOfWeek: [120, 60, 70, 85, 140, 260, 240],
    retention: { uniqueShare: 0.72, returningShare: 0.31, returnRate: 0.31 },
    validations: { totalCodesActive: 6, sessionsWithDiscount: 184 },
    permits: { active: 42, expiringSoon: 3, expired: 1 },
    kpis: { avgDurationMin: 161, activeNow: 7, activeReservedCents: 18_900 },
    peakHour: { hour: 18, sessions: 24, label: '6 PM' },
    payoutInstant: { instantAvailableCents: 142_500, estFeeCents: 2138, externalAccountLast4: '4242' },
  },
}
